import React, {useState,useMemo} from 'react';
import {DataGrid, Expander, useTreeStore} from "@chkp/mwc-data-grid";
import {getUpdatableObjectImageById} from '../../utils/utils';

const SearchField = ({ value, onChange }) => (
	<input
		value={value}
		onChange={(e) => onChange(e.target.value)}
		placeholder='Search..'
	/>
);

//top bar contains search field and slected item
const TopBar = ({ searchTerm, onSearchTermChange,store,selectedKeys }) => {
    const selected = <TopBarRenderer
                        store={store}
                        selectedKeys={selectedKeys}
                    ></TopBarRenderer>
    return (
        <div>
            <div style={{ padding: 5 }}>
                <SearchField value={searchTerm} onChange={onSearchTermChange} />
            </div>
            {selected}
        </div>
    );
};

const TopBarRenderer = ({ store: { getRecordByKey }, selectedKeys }) => {
    const selectedRec = getRecordByKey(selectedKeys?.[0]);
    return (
        <span style={{ color: '#777', padding: 5 }}>
            {`Selected: ${selectedRec?.displayName || 'None'}`}
        </span>
    );
};

const idxRenderer = (cellContext) => {
    const {
        record,
        value,
        style,
        row: {
            selected: rowSelected,
            store: { isExpanded, setExpanded },
        },
    } = cellContext;
    
    // Calculate expander
    const expander= (record.children.length > 0) ? (record.children && (
        <Expander
            expanded={isExpanded(record)}
            onClick={() => {
                setExpanded(record, !isExpanded(record));
            }}
        />
    )) : null;

    const img = <img
        style={{ verticalAlign: 'middle', margin: '-2px 5px 0px 0px', width: '16px' }}
        src={getUpdatableObjectImageById(record.icon)}
        alt=""
    />
    //the structor of entry in the tree
    const content = (
        <>
            {expander}
            {img}
            {value}
        </>
    );

    const newStyle = {
        ...style,
        background: rowSelected ? '#bce3fe' : '#EFF1F3',
        paddingLeft: 16,
    };

    return {
        style: newStyle,
        content : content,
    };
};

const rowRenderer = (rowContext) => {
    const {
        record,
        selected,
        store: { isExpanded, getParentByRecord },
    } = rowContext;
    const ret = {};
    if (record) {
        //show arrow only if it has children
        const hasChildren = record.children;
        const expanded = hasChildren  && isExpanded(record);

        if (expanded && !selected) {
            Object.assign(ret, {
                style: {
                    borderBottom: '1px solid #BAB9B9',
                },
            });
        }
        // Calculate indent of tree view
        let parent = getParentByRecord(record);
        ret.indent = -15;
        while (parent) {
            ret.indent += 15;
            parent = getParentByRecord(parent);
        }
    }
    return ret;
}

const columns = [
    {
        key: 'displayName',
        label: 'Name',
        minWidth: 100,
        resizable: true,
        renderer: idxRenderer,
    }
];

//deep filter in the tree, keeps the structure of the objects
//returns a sub tree containing the search term, while keeping the structore of the tree
function filterTree(array, searchTerm) {
    const getNodes = (result, object) => {
        if (object.displayName.toUpperCase().includes(searchTerm)) {
            result.push(object);
            return result;
        }
        if (Array.isArray(object.children)) {
            const children = object.children.reduce(getNodes, []);
            if (children.length) result.push({ ...object, children });
        }
        return result;
    };
    return array.reduce(getNodes, []);
}

/**
 * Tree component
 * @param {Object} params
 * @param {var} params.data tree data structure. {children[], displayName,icon,objCodeName,uuid}
 * @param {function} params.setSelectedObjects (String) sets the selected Object an updates it in UpdatableOblectSelectModal
 * @param {boolean} params.loading is the data still loading
 */

//selectedObjects is set by the user and return the selected keys to the parent componant
const Tree = ({data,setSelectedObjects,loading,handleApply}) => {
	const [searchTerm, setSearchTerm] = useState('');
    const [selectedKeys, setSelectedKeys] = useState([]);

    const root = useMemo(()=>{
        const ret = {...data};
        ret.children = filterTree(ret.children,searchTerm.toUpperCase());
        return ret;
    },[data,searchTerm]);

    function setSelectedItems(selectedItems) {
        setSelectedObjects(selectedItems);
        setSelectedKeys(selectedItems)
    return ;
    }
    function setSelectedDoubleClick(selectedItems) {
        handleApply(selectedItems);
    return ;
    }

    //set the expender to true when there is a search.
    const defaultExpanded = useMemo(()=>{
        if(searchTerm !== ""){
            return true;
        }else{
            return false;
        }
    },[searchTerm]);

    const store = useTreeStore({
		rowKey: 'objCodeName',
        root,
        defaultExpanded,
        includeRoot : false,
    });

    return (
        <DataGrid
            store={store}
            columns={columns}
            rowRenderer={rowRenderer}
            background={false}
            selectedKeys={selectedKeys}
            loading={loading}
            onSelectedKeysChange={setSelectedItems}
            onRowDoubleClick={setSelectedDoubleClick}
            topBar={
                <TopBar
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                />
            }
        />
    );
};

export default Tree;